import { supportedLang } from "./default";

//https://www.leohuynh.dev/snippets/casing-utils/
export function pascalCaseToPlainText(str: string): string {
  return str.replace(/[A-Z]/g, ($1) => ` ${$1.toLowerCase()}`).trim();
}

export const transparentPixel = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

export const slugify = (str: string) => {
  return String(str)
    .normalize("NFKD") // split accented characters into their base characters and diacritical marks
    .replace(/[\u0300-\u036f]/g, "") // remove all the accents, which happen to be all in the \u03xx UNICODE block.
    .trim() // trim leading or trailing whitespace
    .toLowerCase() // convert to lowercase
    .replace(/[^a-z0-9 -]/g, "") // remove non-alphanumeric characters
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
};

export const toUnicodeLocale = (locale: string, isForSitemap?: boolean) => {
  const defaultRegexp = "^[a-z]{2}(-[A-Z]{0,2})?$";
  const defaultLocale = new RegExp(defaultRegexp);
  if (Object.keys(supportedLang).includes(locale)) {
    // If it's a supported language, append "-ae" to avoid crashing
    locale = isForSitemap ? (locale === "en" || locale === "ar" ? `${locale}-ae` : locale) : `${locale}-ae`;
  }
  if (!defaultLocale.test(locale)) {
    const insensitiveLocale = new RegExp(defaultRegexp, "i");
    if (insensitiveLocale.test(locale)) {
      const availableLocale = locale.split("-");
      const newLocale = `${availableLocale[0].toLowerCase()}-${availableLocale[1].toUpperCase()}`;
      return newLocale;
    }
  }
  return locale;
};
//array intersection
/**
 * let intersection = arrA.filter(x => arrB.includes(x));
 */
export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function encodeUrlIfNeeded(url: string) {
  if (url && decodeURIComponent(url) === url) {
    return encodeURIComponent(url);
  }
  return url;
}

export function getMaskedCard(summary: string) {
  return `**** **** **** ${summary}`;
}
type AnyObject = { [key: string]: any };
export function trimObjectProperties<T>(obj: T): T {
  if (typeof obj === "string") {
    return obj.trim() as T; // Trim strings directly
  } else if (Array.isArray(obj)) {
    return obj.map(trimObjectProperties) as T; // Recursively process arrays
  } else if (obj !== null && typeof obj === "object") {
    const trimmedObject: AnyObject = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        trimmedObject[key] = trimObjectProperties(obj[key]); // Recursively process each property
      }
    }
    return trimmedObject as T;
  }
  return obj; // Return non-string, non-object values as-is
}

// --- START: Enhanced Transliteration and Slugify Function ---
const cyrillicMap: { [key: string]: string } = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "yo",
  ж: "zh",
  з: "z",
  и: "i",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "sch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
  // Uppercase
  А: "A",
  Б: "B",
  В: "V",
  Г: "G",
  Д: "D",
  Е: "E",
  Ё: "Yo",
  Ж: "Zh",
  З: "Z",
  И: "I",
  Й: "Y",
  К: "K",
  Л: "L",
  М: "M",
  Н: "N",
  О: "O",
  П: "P",
  Р: "R",
  С: "S",
  Т: "T",
  У: "U",
  Ф: "F",
  Х: "Kh",
  Ц: "Ts",
  Ч: "Ch",
  Ш: "Sh",
  Щ: "Sch",
  Ъ: "",
  Ы: "Y",
  Ь: "",
  Э: "E",
  Ю: "Yu",
  Я: "Ya",
};

// Simplified Arabic transliteration map.
// This is a basic mapping and might not cover all nuances or dialects.
// For comprehensive Arabic transliteration, a dedicated library is recommended.
const arabicMap: { [key: string]: string } = {
  ا: "a",
  ب: "b",
  ت: "t",
  ث: "th",
  ج: "j",
  ح: "h",
  خ: "kh",
  د: "d",
  ذ: "dh",
  ر: "r",
  ز: "z",
  س: "s",
  ش: "sh",
  ص: "s",
  ض: "d",
  ط: "t",
  ظ: "z",
  ع: "a",
  غ: "gh",
  ف: "f",
  ق: "q",
  ك: "k",
  ل: "l",
  م: "m",
  ن: "n",
  ه: "h",
  و: "w",
  ي: "y",
  ة: "a",
  ى: "a",
  ء: "",
  ئ: "i",
  ؤ: "u",
  أ: "a",
  إ: "i",
  آ: "aa",
  // Numerals (if they appear in headings)
  "٠": "0",
  "١": "1",
  "٢": "2",
  "٣": "3",
  "٤": "4",
  "٥": "5",
  "٦": "6",
  "٧": "7",
  "٨": "8",
  "٩": "9",
  // Diacritics/Harakaat (often removed or ignored in transliteration for slugs)
  "َ": "",
  "ُ": "",
  "ِ": "",
  "ْ": "",
  "ّ": "",
  "ٰ": "",
  "ٔ": "",
  "ٕ": "",
};

export function customSlugifyForNonEnglishText(text: string): string {
  if (!text) return "";

  let slug = text.trim().toLowerCase();

  // First, apply specific language transliteration if the text appears to be in that language.
  // This is a simplistic check; a more robust solution might use language detection.
  // For Arabic (check for common Arabic characters)
  if (/[\u0600-\u06FF]/.test(slug)) {
    slug = slug
      .split("")
      .map((char) => arabicMap[char] || char)
      .join("");
  }
  // For Cyrillic (check for common Cyrillic characters)
  else if (/[\u0400-\u04FF]/.test(slug)) {
    slug = slug
      .split("")
      .map((char) => cyrillicMap[char] || char)
      .join("");
  }

  // General cleanup after language-specific transliteration:
  // Replace sequences of non-alphanumeric (excluding hyphens) with a single hyphen
  // This cleans up spaces, punctuation, and any remaining non-ASCII that weren't transliterated.
  // It ensures the slug only contains a-z, 0-9, and hyphens.
  slug = slug.replace(/[^a-z0-9]+/g, "-");

  // Remove leading/trailing hyphens
  slug = slug.replace(/^-+|-+$/g, "");

  // Limit length to 100 characters AFTER transliteration and basic cleanup
  if (slug.length > 100) {
    slug = slug.substring(0, 100);
    // Re-trim hyphens in case the cut off happened mid-hyphen
    slug = slug.replace(/^-+|-+$/g, "");
  }

  // Ensure it starts with an alphanumeric character if it's not empty
  if (slug && !/^[a-z0-9]/.test(slug)) {
    // If it starts with a hyphen or something weird, prepend a safe character
    // Use 'h' or 'id' as a prefix to ensure it's always a valid HTML ID starting with a letter.
    slug = `h-${slug}`;
  }
  // Final check to ensure it's not empty after all operations
  if (!slug) {
    slug = "heading"; // Fallback for extremely problematic text that results in an empty slug
  }

  return slug;
}

export function formatTextForUrl(text: string) {
  if (!text) return "";

  let formattedText = text.trim();

  // Replace spaces with hyphens
  formattedText = formattedText.replace(/ /g, "-");

  // Trim to 100 characters if longer
  if (formattedText.length > 100) {
    formattedText = formattedText.substring(0, 100);
    // Remove trailing hyphen if the cut happens at a space
    formattedText = formattedText.replace(/-$/, "");
  }

  return formattedText;
}
// --- END: Enhanced Transliteration and Slugify Function ---